import {
  IChecklistCategorieReportChecklistDetail,
  IChecklistsReportDataResponse,
} from "@/interfaces/shares";
import { IAssessmentDetail } from "@/interfaces/assessments/IAssessmentDetails";
import {
  getAllChecklist,
  makeParentNonHierarchical,
  putChecklistRename,
} from "@/services/assessment";
import { showErrorAPI } from "@/services/apiRoutes";
import { Assessment, Checklist, Org } from "@/services/apiRoutes/routes";
import {
  handleChecklistDataReportSplit,
  putChecklistCategoryInfo,
} from "@/services/sharing";
import {
  getChecklistCost,
  getChecklistImpactLevel,
  getChecklistIncidents,
  getChecklistIncidentType,
  getWizardQuestion,
  postChecklistIncident,
  putChecklistIncidents,
} from "@/services/wizard";
import get from "lodash/get";
import {
  checklistStatusId,
  exceptionStatusCodes,
  localStorageKey,
} from "@/utils/constants";
import { putChecklistGroup } from "@/services/recommend";
import { getComponentUiText } from "@/utils/utilities";

const sharingModule = {
  namespaced: true,
  state() {
    return {
      checklists: [] as IAssessmentDetail[],
      checklistReportId: null,
      checklistReportData: {} as IChecklistsReportDataResponse,
      checklistCategories: [] as IChecklistCategorieReportChecklistDetail[],
      isLoading: false,
      loadingSplit: {},
      checklistQuickWin: {} as IChecklistsReportDataResponse,
      checklistRoadMap: [] as IChecklistsReportDataResponse[],
      roadMapErrorData: [],
      servicesData: {},
      checklistIncidents: [],
      isLoadingIncidents: false,
      checklistIncidentType: [],
      checklistImpactLevel: [],
      checklistCost: [],
      isEdit: false,
    };
  },

  getters: {
    getChecklistReportId: (state: any) => state.checklistReportId,
    getChecklists: (state: any) => state.checklists,
    getChecklistReportData: (state: any) => state.checklistReportData,
    getChecklistCategoriesData: (state: any) => state.checklistCategories,
    getChecklistSummary: (state: any) =>
      state.checklistReportData.checklistSummary,
    getOrg: (state: any) => state.checklistReportData.org,
    getLoading: (state: any) => state.isLoading,
    getChecklistQuickWin: (state: any) => state.checklistQuickWin,
    getChecklistRoadMap: (state: any) => state.checklistRoadMap,
    getChecklistIncidents: (state: any) => state.checklistReportData.incidents,
    getLoadingIncidents: (state: any) => state.isLoadingIncidents,
    getChecklistIncidentType: (state: any) => state.checklistIncidentType,
    getChecklistImpactLevel: (state: any) => state.checklistImpactLevel,
    getChecklistCost: (state: any) => state.checklistCost,
  },
  mutations: {
    INITIAL_CHANGE_ORG_USER: (state: any) => {
      // Reset assessment state when changing org or login user.
      state.checklistReportId = "";
    },
    SET_CHECKLISTS_DATA: (state: any, payload: IAssessmentDetail[]) => {
      state.checklists = payload;
    },
    SET_CHECKLIST_REPORT_ID: (state: any, id: number) => {
      state.checklistReportId = id;
    },
    SET_CHECKLIST_REPORT_DATA: (
      state: any,
      payload: IChecklistsReportDataResponse,
    ) => {
      state.checklistReportData = payload;
      state.checklistCategories = payload.checklistCategories || [];
    },
    SET_CHECKLIST_QUICK_WIN: (
      state: any,
      payload: IChecklistsReportDataResponse,
    ) => {
      state.checklistQuickWin = payload;
    },
    SET_CHECKLIST_ROAD_MAP: (
      state: any,
      payload: IChecklistsReportDataResponse[],
    ) => {
      state.checklistRoadMap = payload || [];
    },
    SET_SERVICES_DATA: (state: any, payload: any) => {
      state.servicesData = payload;
    },
    SET_CHECKLIST_INCIDENTS: (state: any, payload: any) => {
      state.checklistIncidents = payload.map((item: any) => {
        return {
          ...item,
          hasChanged: false,
        };
      });
    },
    RESET_SHARING_STORE: (state: any) => {
      state.checklists = [];
      state.checklistReportId = null;
      state.checklistReportData = {};
      state.isLoading = false;
      state.isLoadingIncidents = false;
      state.checklistQuickWin = {};
      state.checklistRoadMap = [];
      state.servicesData = {};
    },
    ADD_CHECKLIST_INCIDENTS: (state: any, payload: any) => {
      state.checklistIncidents.push(payload);
    },
    REMOVE_CHECKLIST_INCIDENTS: (state: any, payload: any) => {
      state.checklistIncidents = state.checklistIncidents.filter(
        (item: any) => {
          if (payload.id) {
            return item.id !== payload.id;
          } else {
            return item.incidentId !== payload.incidentId;
          }
        },
      );
    },
    SET_CHECKLIST_INCIDENT_TYPE: (state: any, payload: any) => {
      state.checklistIncidentType = payload;
    },
    SET_CHECKLIST_IMPACT_LEVEL: (state: any, payload: any) => {
      state.checklistImpactLevel = payload;
    },
    SET_CHECKLIST_COST: (state: any, payload: any) => {
      state.checklistCost = payload;
    },
    SET_IS_EDITS: (state: any, payload: boolean) => {
      state.isEdit = payload;
    },
    SET_ROAD_MAP_ERROR_DATA: (state: any, payload: any) => {
      state.roadMapErrorData = payload;
    },
    SET_LOADING_SPLIT_CHECKLIST_REPORT: (state: any, payload: any) => {
      const { key, value } = payload;
      state.loadingSplit[key] = value;
    },
    ADD_CHECKLIST_REPORT: async (state: any, data: any) => {
      state.checklistReportData = {
        ...state.checklistReportData,
        ...data,
      };
      state.checklistCategories =
        data.checklistCategories ?? state.checklistCategories;
    },
  },
  actions: {
    resetSharingStore: async ({ commit }: any) => {
      commit("RESET_SHARING_STORE");
    },
    updateRoadMapItemData: async ({ state }: any, payload) => {
      try {
        const params = {
          checklistId: state.checklistReportId,
          ...payload,
        };
        const res = await putChecklistGroup(params);
        state.checklistRoadMap = state.checklistRoadMap.map((item: any) => {
          if (item.checklistGroupId === payload.checklistGroupId) {
            return {
              ...item,
              checklistGroupName: res.data.data.name,
              checklistGroupActions: res.data.data.actions,
            };
          } else {
            return item;
          }
        });
        return res.data.data;
      } catch (error) {
        const extendText = getComponentUiText(
          "app-share-cyber-profile-extend-error-messages-1",
          false,
          {
            name: getComponentUiText("app-share-cyber-profile-roadmap-title-1"),
          },
        );
        showErrorAPI(
          error,
          Checklist.updateChecklistItemGroup,
          exceptionStatusCodes.BadGateway,
          extendText,
        );
      }
    },
    fetchChecklistsData: async ({ commit }: any) => {
      try {
        const res = await getAllChecklist();
        const data = res.data.data;
        // const checklistData = data.filter(
        //   (item: any) =>
        //     !item.checklistIsArchived ||
        //     item.checklistStatusId !== checklistStatusId.defunct,
        // );
        data.sort((a, b) => b.checklistId - a.checklistId);
        // make parent is non hierarchical const parents = makeParentNonHierarchical(e.checklistParent, []);
        const checklists = data.map((e: any) => {
          if (!e.checklistParent) {
            return e;
          }
          const parentsData = makeParentNonHierarchical(e.checklistParent, []);
          const parents = parentsData.filter(
            (item: any) =>
              item.checklistIsArchived === false ||
              item.checklistStatusId !== checklistStatusId.defunct,
          );
          return {
            ...e,
            checklistParent: parents,
          };
        });
        const checklistTreeWithoutArchived: any = [];
        const tempChecklistTreeWithoutArchived = checklists.filter(
          (item: any) =>
            item.checklistIsArchived === false ||
            item.checklistStatusId !== checklistStatusId.defunct,
        );
        checklists.forEach((item: any) => {
          if (
            item.checklistIsArchived === false ||
            item.checklistStatusId !== checklistStatusId.defunct
          ) {
            checklistTreeWithoutArchived.push(item);
          } else {
            let isFoundParent = false;
            if (item.checklistParent && item.checklistParent.length > 0) {
              // find parent of item which is not parent of any other chain
              let oldChain = item.checklistParent;
              item.checklistParent.forEach((child: any) => {
                if (
                  !isFoundParent &&
                  tempChecklistTreeWithoutArchived.length > 0
                ) {
                  const parent = tempChecklistTreeWithoutArchived?.find(
                    (parentItem: any) =>
                      parentItem.checklistId === child.checklistId ||
                      (parentItem.checklistParent &&
                        parentItem.checklistParent.find(
                          (e: any) => e.checklistId === child.checklistId,
                        )),
                  );
                  oldChain = oldChain.filter(
                    (e: any) => e.checklistId !== child.checklistId,
                  );
                  if (!parent) {
                    isFoundParent = true;
                    checklistTreeWithoutArchived.push({
                      ...child,
                      checklistParent: oldChain,
                    });
                  }
                }
              });
            }
          }
        });
        const sortChecklist = checklistTreeWithoutArchived.sort(
          (a, b) =>
            new Date(b.checklistDateCreated).getTime() -
            new Date(a.checklistDateCreated).getTime(),
        );
        commit("SET_CHECKLISTS_DATA", sortChecklist ?? []);
        return sortChecklist || [];
      } catch (error) {
        showErrorAPI(error, Assessment.getChecklists);
        commit("SET_CHECKLISTS_DATA", []);
        return [];
      }
    },
    fetchChecklistReportData: async ({ state, commit }) => {
      try {
        if (!state.checklistReportId) {
          commit("SET_CHECKLIST_REPORT_DATA", {});
          return;
        }
        const displayOptionStorage = localStorage.getItem(
          localStorageKey.cyberReportDisplayOption,
        );
        const displayOption = JSON.parse(displayOptionStorage ?? "{}");

        state.loadingSplit = {
          [Checklist.getChecklistReportDataSummary]: true,
          [Checklist.getChecklistReportDataMetrics]: true,
          [Checklist.getChecklistReportDataForecasts]: true,
          [Checklist.getChecklistReportDataHistory]: true,
          [Checklist.getChecklistReportDataActions]: true,
          [Checklist.getChecklistReportDataCategories]: true,
          [Checklist.getChecklistReportDataInfographics]: true,
          [Checklist.getChecklistReportDataIncidents]: true,
        };

        state.checklistReportData = {};
        state.checklistCategories = [];
        state.checklistQuickWin = [];
        state.checklistRoadMap = [];

        await handleChecklistDataReportSplit(
          Checklist.getChecklistReportDataSummary,
          state.checklistReportId,
        );
        await handleChecklistDataReportSplit(
          Checklist.getChecklistReportDataMetrics,
          state.checklistReportId,
        );
        await handleChecklistDataReportSplit(
          Checklist.getChecklistReportDataForecasts,
          state.checklistReportId,
          displayOption.progress,
        );
        await handleChecklistDataReportSplit(
          Checklist.getChecklistReportDataHistory,
          state.checklistReportId,
          displayOption?.progress || displayOption?.achievementsAndProgress,
        );
        await handleChecklistDataReportSplit(
          Checklist.getChecklistReportDataActions,
          state.checklistReportId,
          displayOption?.quickWins || displayOption?.roadMap,
        );
        await handleChecklistDataReportSplit(
          Checklist.getChecklistReportDataCategories,
          state.checklistReportId,
          displayOption.trafficLights || displayOption.assessmentStats,
        );
        await handleChecklistDataReportSplit(
          Checklist.getChecklistReportDataIncidents,
          state.checklistReportId,
          displayOption.incidentsAndEvents,
        );
        await handleChecklistDataReportSplit(
          Checklist.getChecklistReportDataInfographics,
          state.checklistReportId,
          displayOption.assessmentStats,
        );
      } catch (e) {
        showErrorAPI(e, Checklist.getChecklistReportData);
        commit("SET_CHECKLIST_REPORT_DATA", {});
      }
    },
    fetchServicesData: async ({ state, commit }) => {
      try {
        state.isLoading = true;
        const res = await getWizardQuestion("sections[]=services");
        const data = get(res, "data.data", {});
        commit("SET_SERVICES_DATA", data);
      } catch (e) {
        showErrorAPI(e, Org.getOrgSetup);
      } finally {
        state.isLoading = false;
      }
    },
    setChecklistReportId: ({ commit }: any, id: number) => {
      commit("SET_CHECKLIST_REPORT_ID", id);
    },
    updateChecklistNote: async (_, { checklistId, params }) => {
      try {
        const res = await putChecklistRename(checklistId, params);
        return res;
      } catch (e) {
        const extendText = getComponentUiText(
          "app-share-cyber-profile-extend-error-messages-1",
          false,
          {
            name: params.checklistNotes
              ? getComponentUiText("app-share-cyber-profile-progress-title-1")
              : getComponentUiText("app-share-cyber-profile-progress-title-1"),
          },
        );
        showErrorAPI(
          e,
          Assessment.checklistRename,
          exceptionStatusCodes.BadGateway,
          extendText,
        );
      }
    },
    fetchChecklistIncidents: async ({ state, commit }) => {
      try {
        state.isLoadingIncidents = true;
        const res = await getChecklistIncidents();
        const data = get(res, "data.data.incidents", {});
        commit("SET_CHECKLIST_INCIDENTS", data);
      } catch (e) {
        showErrorAPI(e, Org.getOrgSetup);
      } finally {
        state.isLoadingIncidents = false;
      }
    },
    postChecklistIncidents: async ({ state }, params) => {
      try {
        state.isLoading = true;
        await postChecklistIncident(params);
      } catch (e) {
        const extendText = getComponentUiText(
          "app-share-cyber-profile-extend-error-messages-1",
          false,
          {
            name: getComponentUiText(
              "app-share-cyber-profile-display-option-incidents-events-1",
            ),
          },
        );
        showErrorAPI(
          e,
          Org.postOrgIncident,
          exceptionStatusCodes.BadGateway,
          extendText,
        );
      } finally {
        state.isLoading = false;
      }
    },
    updateChecklistIncidents: async ({ state }, params) => {
      try {
        state.isLoading = true;
        await putChecklistIncidents(params);
      } catch (e) {
        const extendText = getComponentUiText(
          "app-share-cyber-profile-extend-error-messages-1",
          false,
          {
            name: getComponentUiText(
              "app-share-cyber-profile-display-option-incidents-events-1",
            ),
          },
        );
        showErrorAPI(
          e,
          Org.putOrgIncidents,
          exceptionStatusCodes.BadGateway,
          extendText,
        );
      } finally {
        state.isLoading = false;
      }
    },
    fetchChecklistIncidentType: async ({ state, commit }) => {
      try {
        state.isLoadingIncidents = true;
        const res = await getChecklistIncidentType();
        const data = get(res, "data.data", {});
        commit("SET_CHECKLIST_INCIDENT_TYPE", data);
      } catch (e) {
        showErrorAPI(e, Org.getOrgIncidentType);
      } finally {
        state.isLoadingIncidents = false;
      }
    },
    fetchChecklistImpactLevel: async ({ state, commit }) => {
      try {
        state.isLoadingIncidents = true;
        const res = await getChecklistImpactLevel();
        const data = get(res, "data.data.impact", {});
        commit("SET_CHECKLIST_IMPACT_LEVEL", data);
      } catch (e) {
        showErrorAPI(e, Checklist.getChecklistImpactLevel);
      } finally {
        state.isLoadingIncidents = false;
      }
    },
    fetchChecklistCost: async ({ state, commit }) => {
      try {
        state.isLoadingIncidents = true;
        const res = await getChecklistCost();
        const data = get(res, "data.data.cost", {});
        commit("SET_CHECKLIST_COST", data);
      } catch (e) {
        showErrorAPI(e, Checklist.getChecklistCost);
      } finally {
        state.isLoadingIncidents = false;
      }
    },
    updateChecklistTrafficLight: async ({ state }, params) => {
      try {
        await params.forEach(async (item) => {
          try {
            await putChecklistCategoryInfo(
              state.checklistReportId,
              item.checklistCategoryId,
              item.checklistCategoryNotes,
            );
          } catch (error) {
            const idx = state.checklistCategories.findIndex(
              (e: any) => e.checklistCategoryId === item.checklistCategoryId,
            );
            if (idx >= 0) {
              state.checklistCategories[idx].checklistCategoryNotes =
                item.checklistCategoryNotesOld;
            }
            const extendText = getComponentUiText(
              "app-share-cyber-profile-extend-error-messages-1",
              false,
              {
                name: getComponentUiText(
                  "app-share-cyber-profile-traffic-lights-title-1",
                ),
              },
            );
            showErrorAPI(
              error,
              Checklist.putChecklistCategoryInfo,
              exceptionStatusCodes.BadGateway,
              extendText,
            );
          }
        });
      } catch (error) {
        showErrorAPI(error, Checklist.putChecklistCategoryInfo);
      }
    },
  },
};
export default sharingModule;
